// src/modules/app-views/services/pages/Document/tabs/ParametersTab/modals/CommonProblemModal.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Flex, Space, Form, Input } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CommonProblemModal = ({ open, onCancel, onSave, initialData, loading }) => {
  // ---------------------------------------------------------------------------
  // 1. HOOKS & REFS
  // ---------------------------------------------------------------------------
  const [form] = Form.useForm();
  const inputRef = useRef(null);

  // ---------------------------------------------------------------------------
  // 2. LOCAL VARIABLES
  // ---------------------------------------------------------------------------
  const isEditing = !!initialData;
  const title = isEditing ? translateX('edit_item') : translateX('add_problem');

  // ---------------------------------------------------------------------------
  // 3. EFFECTS
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (open) {
      if (initialData) {
        form.setFieldsValue({
          problem: initialData.problem || '',
          cause: initialData.cause || '',
          solution: initialData.solution || '',
        });
      } else {
        form.resetFields();
      }

      // auto-focus on problem field
      setTimeout(() => {
        inputRef.current?.focus();
      }, 150);
    }
  }, [open, initialData, form]);

  // ---------------------------------------------------------------------------
  // 4. HANDLERS
  // ---------------------------------------------------------------------------
  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      onSave({
        problem: (values.problem || '').trim(),
        cause: (values.cause || '').trim(),
        solution: (values.solution || '').trim(),
      });
    } catch (error) {
      // validation failed - form will show errors
    }
  }, [form, onSave]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    onCancel();
  }, [form, onCancel]);

  const handleAfterClose = useCallback(() => {
    form.resetFields();
  }, [form]);

  // ---------------------------------------------------------------------------
  // 5. UI
  // ---------------------------------------------------------------------------
  return (
    <Modal
      title={title}
      open={open}
      onCancel={handleCancel}
      afterClose={handleAfterClose}
      destroyOnHidden
      centered
      getContainer={document.body}
      width={560}
      footer={
        <Flex justify="end" style={{ paddingTop: 8 }}>
          <Space>
            <Button onClick={handleCancel} disabled={loading} icon={<CloseOutlined />}>
              {translateX('cancel')}
            </Button>
            <Button
              type="primary"
              loading={loading}
              icon={<CheckOutlined />}
              onClick={handleSubmit}
            >
              {translateX('save')}
            </Button>
          </Space>
        </Flex>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ problem: '', cause: '', solution: '' }}
      >
        <Form.Item
          name="problem"
          label={translateX('problem_description')}
          rules={[{ required: true, message: translateX('required_field') }]}
        >
          <Input
            ref={inputRef}
            maxLength={200}
            placeholder={translateX('problem_description')}
            disabled={loading}
          />
        </Form.Item>

        <Form.Item
          name="cause"
          label={translateX('cause_description')}
        >
          <Input.TextArea
            rows={2}
            maxLength={500}
            showCount
            placeholder={translateX('cause_description')}
            disabled={loading}
          />
        </Form.Item>

        <Form.Item
          name="solution"
          label={translateX('solution_description')}
        >
          <Input.TextArea
            rows={2}
            maxLength={500}
            showCount
            placeholder={translateX('solution_description')}
            disabled={loading}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CommonProblemModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************
